import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RootLayout } from '@/app/layouts/RootLayout';
import { AppLayout } from '@/app/layouts/AppLayout';
import { RequireAuth } from '@/app/guards/RequireAuth';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { SignupPage } from '@/features/auth/pages/SignupPage';
import { VerifyEmailPage } from '@/features/auth/pages/VerifyEmailPage';
import { TenantPickerPage } from '@/features/auth/pages/TenantPickerPage';
import { PasswordResetRequestPage } from '@/features/auth/pages/PasswordResetRequestPage';
import { PasswordResetConfirmPage } from '@/features/auth/pages/PasswordResetConfirmPage';
import { AcceptInvitePage } from '@/features/auth/pages/AcceptInvitePage';
import { SessionsPage } from '@/features/auth/pages/SessionsPage';
import { OnboardingPage } from '@/features/onboarding/OnboardingPage';
import { PrivacyPage, TermsPage } from '@/features/legal/LegalPage';
import { CommandCenterPage } from '@/features/command-center/pages/CommandCenterPage';
import { AssetListPage } from '@/features/operations/pages/AssetListPage';
import { AssetDetailPage } from '@/features/operations/pages/AssetDetailPage';
import { MaintenancePage } from '@/features/operations/pages/MaintenancePage';
import { EngagementsPage } from '@/features/operations/pages/EngagementsPage';
import { AttendancePage } from '@/features/operations/pages/AttendancePage';
import { AttendanceSettingsPage } from '@/features/operations/pages/AttendanceSettingsPage';
import { FuelPage } from '@/features/operations/pages/FuelPage';
import { AssetTypeManagerPage } from '@/features/operations/pages/AssetTypeManagerPage';
import { DocumentTypeManagerPage } from '@/features/operations/pages/DocumentTypeManagerPage';
import { FieldUsersPage } from '@/features/field-users/pages/FieldUsersPage';
import { CardPage } from '@/features/field-users/pages/CardPage';
import { ReportsPage } from '@/features/reports/pages/ReportsPage';
import { PayrollPage } from '@/features/financial/pages/PayrollPage';
import { SalaryFormulaPage } from '@/features/financial/pages/SalaryFormulaPage';
import { CommissionRulesPage } from '@/features/financial/pages/CommissionRulesPage';
import { FinesPage } from '@/features/financial/pages/FinesPage';
import { AdvancesPage } from '@/features/financial/pages/AdvancesPage';
import { ReconciliationPage } from '@/features/financial/pages/ReconciliationPage';
import { SettingsPage } from '@/features/settings/pages/SettingsPage';
import { UsersPage } from '@/features/settings/pages/UsersPage';
import { RolesPage } from '@/features/settings/pages/RolesPage';
import { CommsPage } from '@/features/comms/pages/CommsPage';
import { TenantConfigPage } from '@/features/tenant-config/pages/TenantConfigPage';
import { AlertRulesPage } from '@/features/tenant-config/pages/AlertRulesPage';
import { AuditLogPage } from '@/features/audit/pages/AuditLogPage';
import { NotificationPreferencesPage } from '@/features/notifications/pages/NotificationPreferencesPage';
import { NotFoundPage } from '@/app/pages/NotFoundPage';

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public, unauthenticated entry points
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      { path: '/verify-email/:token', element: <VerifyEmailPage /> },
      { path: '/select-tenant', element: <TenantPickerPage /> },
      { path: '/reset-password', element: <PasswordResetRequestPage /> },
      { path: '/reset-password/:token', element: <PasswordResetConfirmPage /> },
      { path: '/accept-invite/:token', element: <AcceptInvitePage /> },

      // Legal — public so they can be linked from /signup and marketing.
      { path: '/terms', element: <TermsPage /> },
      { path: '/privacy', element: <PrivacyPage /> },

      // Authenticated app
      {
        element: <RequireAuth />,
        children: [
          { path: '/onboarding', element: <OnboardingPage /> },
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to="/command-center" replace /> },
              { path: '/command-center', element: <CommandCenterPage /> },
              { path: '/assets', element: <AssetListPage /> },
              { path: '/assets/:assetId', element: <AssetDetailPage /> },
              { path: '/maintenance', element: <MaintenancePage /> },
              { path: '/engagements', element: <EngagementsPage /> },
              { path: '/fuel', element: <FuelPage /> },
              { path: '/attendance', element: <AttendancePage /> },
              { path: '/field-users', element: <FieldUsersPage /> },
              // Short, memorable setup-card link — /card/A-001
              { path: '/card/:employeeId', element: <CardPage /> },
              { path: '/payroll', element: <PayrollPage /> },
              { path: '/advances', element: <AdvancesPage /> },
              { path: '/fines', element: <FinesPage /> },
              { path: '/reconciliation', element: <ReconciliationPage /> },
              { path: '/reports', element: <ReportsPage /> },
              { path: '/comms', element: <CommsPage /> },
              { path: '/settings/tenant-config', element: <TenantConfigPage /> },
              { path: '/settings/alert-rules', element: <AlertRulesPage /> },
              { path: '/settings/salary-formulas', element: <SalaryFormulaPage /> },
              { path: '/settings/commission-rules', element: <CommissionRulesPage /> },
              { path: '/settings', element: <SettingsPage /> },
              { path: '/settings/users', element: <UsersPage /> },
              { path: '/settings/roles', element: <RolesPage /> },
              { path: '/settings/attendance', element: <AttendanceSettingsPage /> },
              { path: '/settings/asset-types', element: <AssetTypeManagerPage /> },
              { path: '/settings/document-types', element: <DocumentTypeManagerPage /> },
              { path: '/settings/sessions', element: <SessionsPage /> },
              { path: '/settings/audit-log', element: <AuditLogPage /> },
              {
                path: '/settings/notifications',
                element: <NotificationPreferencesPage />,
              },
            ],
          },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
